import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Cloud, FlaskConical, KeyRound, ShieldCheck, Cpu, Info,
} from 'lucide-react';
import CloudConfig from './CloudConfig';
import { CloudConfigProvider } from '../../context/CloudConfigContext';
import SandboxVault from '../orchestration/SandboxVault';
import SecretBrokers from './SecretBrokers';
import Policies from './Policies';
import RunnerFleet from './RunnerFleet';

// Tabbed hub — makes it visually obvious WHICH credential feeds WHICH engine.
// - Connected: prod read-only creds → discovery, cost, cross-cloud
// - Sandbox Vault: Tier 2 encrypted creds → Confidence Lab apply/destroy
// - Secret Brokers: prod write creds via HashiCorp Vault / AWS SM / Azure KV
// - CGPL Policies: governance rules that gate applies
const TABS = [
  {
    k: 'connected',
    label: 'Connected Mode',
    icon: Cloud,
    color: 'text-emerald-300',
    accent: 'border-emerald-500/40 bg-emerald-500/10',
    subtitle: 'Read-only prod credentials — power discovery, cost analytics, cross-cloud views.',
    Component: CloudConfig,
  },
  {
    k: 'sandbox-vault',
    label: 'Sandbox Vault',
    icon: FlaskConical,
    color: 'text-purple-300',
    accent: 'border-purple-500/40 bg-purple-500/10',
    subtitle: 'Fernet-encrypted sandbox-only creds — used by the Confidence Lab (Tier 2 apply/destroy).',
    Component: SandboxVault,
  },
  {
    k: 'secret-brokers',
    label: 'Secret Brokers',
    icon: KeyRound,
    color: 'text-amber-300',
    accent: 'border-amber-500/40 bg-amber-500/10',
    subtitle: 'Prod write access via HashiCorp Vault / AWS SM / Azure KV — never stored on the platform.',
    Component: SecretBrokers,
  },
  {
    k: 'policies',
    label: 'CGPL Policies',
    icon: ShieldCheck,
    color: 'text-cyan-300',
    accent: 'border-cyan-500/40 bg-cyan-500/10',
    subtitle: 'Deterministic governance rules that gate every plan/apply — cost, security, compliance.',
    Component: Policies,
  },
  {
    k: 'runner-fleet',
    label: 'Runner Fleet',
    icon: Cpu,
    color: 'text-purple-300',
    accent: 'border-purple-500/40 bg-purple-500/10',
    subtitle: 'Live view of every runner worker + queue depth. Toggle between embedded and service mode.',
    Component: RunnerFleet,
  },
];

export default function CloudConfigHub() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = (() => {
    const qs = new URLSearchParams(location.search).get('tab');
    if (qs && TABS.find((t) => t.k === qs)) return qs;
    return 'connected';
  })();
  const [tab, setTab] = useState(initialTab);

  const current = TABS.find((t) => t.k === tab) || TABS[0];
  const Body = current.Component;

  const switchTab = (k) => {
    setTab(k);
    // Reflect selection in URL so users can deep-link.
    const params = new URLSearchParams(location.search);
    params.set('tab', k);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  return (
    <CloudConfigProvider>
    <div className="min-h-screen text-slate-100" data-testid="cloud-config-hub">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-4">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">
              Admin
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight"
                data-testid="cloud-config-hub-heading">
              Cloud Config Hub
            </h1>
            <p className="text-slate-400 mt-2 max-w-2xl text-sm">
              One place to manage every credential and policy CloudGenie uses,
              grouped by which engine it feeds.
            </p>
          </div>
          <div className="text-xs text-slate-500 border border-slate-800 rounded-lg px-3 py-2 max-w-md flex items-start gap-2"
                data-testid="cloud-config-hub-legend">
            <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
            <div>
              <div className="text-slate-300 font-semibold mb-0.5">Which credential goes where?</div>
              <div>
                <b className="text-emerald-300">Connected</b> = read-only prod ·&nbsp;
                <b className="text-purple-300">Sandbox Vault</b> = Tier 2 apply ·&nbsp;
                <b className="text-amber-300">Brokers</b> = prod writes ·&nbsp;
                <b className="text-cyan-300">Policies</b> = gates.
              </div>
            </div>
          </div>
        </div>

        {/* Tab strip */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3"
              data-testid="cloud-config-hub-tabstrip">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.k;
            return (
              <button
                key={t.k}
                onClick={() => switchTab(t.k)}
                data-testid={`cch-tab-${t.k}`}
                className={`text-left rounded-xl border p-4 transition-all group ${
                  active
                    ? `${t.accent} ring-1 ring-current ${t.color}`
                    : 'border-slate-800 hover:border-slate-700 hover:bg-slate-900/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${active ? t.color : 'text-slate-500'}`} />
                  <div className={`text-sm font-semibold ${active ? t.color : 'text-slate-200'}`}>
                    {t.label}
                  </div>
                </div>
                <div className="text-xs text-slate-400 leading-relaxed">
                  {t.subtitle}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mount the active tab's page component. It renders its own layout,
          so we drop it inside a divider without extra padding. */}
      <div className="border-t border-slate-800/70 mt-4" data-testid={`cch-body-${current.k}`}>
        <Body />
      </div>
    </div>
    </CloudConfigProvider>
  );
}
